import type { Session } from '@getaccept/lib-shared/src/types/Session';
import { computed, ref } from '@vue/composition-api';
import { defineStore } from 'pinia';
import { UserRoleLegacy } from '@getaccept/lib-shared/src/users/types/user-role-legacy';
import { useStorage } from '@vueuse/core';
import { getCurrency } from '@getaccept/lib-shared/src/helpers/currency';
import { SessionStorageKey } from '@getaccept/lib-shared/src/session/type/enums/session-storage-keys';
import type { SessionResponse } from '../../api/session/types/session-response';
import { SessionService } from '../../api/session/services/session.service';

export const useSessionStore = defineStore('session', () => {
  const session = useStorage<Session>(SessionStorageKey.Session, {});
  const sessionIsLoaded = ref(false);
  const sessionIsLoading = ref(false);
  const sessionIsError = ref(false);

  const fetchSession = async () => {
    sessionIsLoading.value = true;
    sessionIsError.value = false;
    try {
      const response: SessionResponse = await SessionService.getSession(true);
      if (!response) {
        throw new Error();
      }

      session.value = response.session;
      localStorage.setItem('GA_ACTIVE_ENTITY', session.value.entity_hash);
      sessionIsLoaded.value = true;
    } catch {
      console.error('Could not load session');
      sessionIsError.value = true;
    } finally {
      sessionIsLoading.value = false;
    }
  };

  const updateSession = (updatedSession: Partial<Session>) => {
    session.value = { ...session.value, ...updatedSession };
  };

  const currency = computed(() => getCurrency(session.value.currency) || getCurrency('USD'));
  const showManager = computed(() =>
    [UserRoleLegacy.Manager, UserRoleLegacy.Admin].includes(session.value.user_role)
  );
  const showAdmin = computed(() => UserRoleLegacy.Admin === session.value.user_role);
  const isRestrictedUser = computed(
    () => session.value.security_restrict && UserRoleLegacy.User === session.value.user_role
  );

  return {
    session,
    sessionIsLoaded,
    sessionIsLoading,
    sessionIsError,
    fetchSession,
    updateSession,
    currency,
    showManager,
    showAdmin,
    isRestrictedUser,
  };
});
